import { fromWebSerial } from '@rxjs-ninja/rxjs-utility';
import { hasValue } from 'Lib/Any';
import { nanoid } from 'nanoid';
import { from, iif, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

/**
 * Store registered ports in memory, keyed by an internally-generated id (as we cannot obtain an identifer for the port with the serial API)
 */
let ports: { [portId: string]: SerialPort } = {};

/**
 * Register ports for an external id.
 *
 * Ports can be registered to multiple external ids
 */
let portRegistry: { [id: string]: string } = {};

/**
 * Register a friendly name for each external id (for use in messages, etc.)
 */
let friendlyNameRegistry: { [id: string]: string } = {};

/**
 * Returns the internal port identifier registered for the given external id
 */
export const getPortId = (id: string) => portRegistry[id];

/**
 * Returns a friendly name for the given external id
 */
export const getFriendlyName = (id: string) => friendlyNameRegistry[id];

/**
 * Returns all external ids the given port is registered to
 */
export const getRegisteredIdsFromPort = (port: SerialPort) =>
  Object.entries(portRegistry)
    .filter(([_, v]) => ports[v] === port)
    .map(([k, _]) => k);

/**
 * Prompts the user to select a port and stores it in memory
 *
 * @param id Identifier to register port with
 * @returns An observable that emits when port has been requested
 * */
export const requestPort = (id: string, friendlyName: string) =>
  from(navigator.serial.requestPort()).pipe(
    tap((p) => {
      let portId = Object.keys(ports).find((k) => ports[k] === p);
      if (!hasValue(portId)) {
        portId = nanoid();
        ports[portId] = p;
      }
      portRegistry[id] = portId!;
      friendlyNameRegistry[id] = friendlyName;
    }),
    map((_) => undefined as void)
  );

/**
 * Tests whether the port can be opened using the provided options
 *
 * @param id Identifier to register port with
 * @param options Connection options
 * @returns An observable that emits when port has been successfully tested
 */
export const testPortConnection = (id: string, options: SerialOptions) =>
  obtainPortIfNotOpen(id).pipe(
    switchMap((port) => from(port.open(options)).pipe(switchMap((_) => from(port.close()))))
  );

/**
 * Unregisters a port (if not open)
 *
 * @param id Identifier port was registered with
 * @returns An observable that emits when port is unregistered
 * */
export const unregisterPort = (id: string) => {
  let portId = portRegistry[id];
  if (!portId) return throwError(new Error(`No port has been registered for id "${id}".`));

  // If port is registered to another id, keep it around
  if (Object.entries(portRegistry).some(([k, v]) => k !== id && v === portId)) {
    delete portRegistry[id];
    delete friendlyNameRegistry[id];
    return of(undefined as void);
  }

  // If this is the only id port is registered with, attempt to remove it if it is not in use. Fail if we cannot.
  return obtainPortIfNotOpen(id).pipe(
    tap((_) => {
      delete portRegistry[id];
      delete friendlyNameRegistry[id];
      delete ports[portId];
    }),
    map((_) => undefined as void)
  );
};

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8');

/**
 * Reads from a previously registered port.
 *
 * Handles opening and closing of port, and allows us to send values while we are reading.
 *
 * Wraps around library method {@link fromWebSerial} with an easier interface for us to work with
 *
 * @param id Identifier to register port with
 * @param options Connection options
 * @param send$ An observable which emits string values that will be sent via the port during reading
 * @param abort$ An observable which, when it emits, will trigger us to cancel the read
 * @returns An observable which emits decoded string values read in from the serial port
 */
export const read = (
  id: string,
  options: SerialOptions,
  send$: Observable<string | undefined>,
  abort$: Observable<void>
) => {
  let abortController = new AbortController();
  abort$.pipe(tap(() => abortController.abort())).subscribe();

  return obtainPortIfNotOpen(id).pipe(
    switchMap((port) =>
      fromWebSerial(port, send$.pipe(map((s) => encoder.encode(s))), options, abortController.signal).pipe(
        map((a) => decoder.decode(a))
      )
    )
  );
};

/**
 * Helper to perform check that port is registered and open for use.
 *
 * @param id Identifier port was registered with
 * @returns An observable that emits the requested port if not currently open (in use).
 * Emits errors if port is not registered or is open.
 */
const obtainPortIfNotOpen = (id: string) =>
  obtainPort(id).pipe(
    switchMap((port) =>
      isPortOpen(port).pipe(
        switchMap((isOpen) =>
          iif(() => isOpen, throwError(new Error(`The port registered for id "${id}" is in use.`)), of(port))
        )
      )
    )
  );

const obtainPort = (id: string) =>
  of(ports[portRegistry[id]]) ?? throwError(new Error(`No port has been registered for id "${id}".`));

/**
 * Determine if port is open
 *
 * From the Web Serial API specification https://wicg.github.io/serial/
 * there is no easy interface to get the current state of the port.
 *
 * The cleanest way I found was to use the specification that getSignals throws a specific error
 * when state is not open. Hopefully we can find a better way one day.
 *
 * @param port
 * @returns An observable which emits true or false once we have determined if the port is open
 */
const isPortOpen = (port: SerialPort) =>
  from(port.getSignals()).pipe(
    map((_) => true),
    catchError((e: Error) =>
      iif(() => e instanceof DOMException && e.code === DOMException.INVALID_STATE_ERR, of(false), throwError(e))
    )
  );
